import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  IconButton,
  Link,
  Toolbar,
  Typography,
} from '@mui/material';
import { Close, ExpandLess, ExpandMore, ThumbDown, ThumbUp } from '@mui/icons-material';
import toast from 'react-hot-toast';

import { PhotoHistoryItem, PhotoModerationItem, PhotoModerationService } from 'us-web-services';
import { colors } from 'us-react-components/dist/constants/Styles';
import { usePhotoModerationKeyboard } from './usePhotoModerationKeyboard';
import { DEFAULT_BORDER_COLOR, STATUS_BORDER_COLOR, STATUS_CHIP_COLOR } from './constants';

interface Props {
  open: boolean;
  onClose: () => void;
}

function PhotoModerationModal({ open, onClose }: Props) {
  const [current, setCurrent] = useState<PhotoModerationItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [history, setHistory] = useState<PhotoHistoryItem[]>([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const fetchNext = useCallback(async () => {
    setLoading(true);
    try {
      const response = await PhotoModerationService.getNext();
      const item = response.data.data;

      setCurrent(item ?? null);
      setQueueEmpty(!item);
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 404 || status === 204) {
        setCurrent(null);
        setQueueEmpty(true);
      } else {
        console.warn('Failed to fetch next moderation item:', err);
        toast.error('Failed to load next photo');
        setCurrent(null);
        setQueueEmpty(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await PhotoModerationService.getStats();

      setPendingCount(response.data.data.pendingCount);
    } catch (err) {
      console.warn('Failed to fetch moderation stats:', err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setReviewedCount(0);
      fetchNext();
      fetchStats();
    }
  }, [open, fetchNext, fetchStats]);

  useEffect(() => {
    const userId = current?.userId;

    setHistory([]);
    setHistoryExpanded(false);
    if (!userId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const response = await PhotoModerationService.getHistory(userId);

        if (cancelled) return;
        const data = response.data.data;
        const items = Array.isArray(data) ? data : [];

        setHistory(items);
        setHistoryExpanded(items.length > 2);
      } catch (err) {
        console.warn('Failed to fetch photo history:', err);
        if (!cancelled) {
          setHistory([]);
          setHistoryExpanded(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [current?.id, current?.userId]);

  const handleApprove = useCallback(async () => {
    if (!current || loading) return;
    setLoading(true);
    try {
      await PhotoModerationService.moderate(current.id, 'APPROVED');
      toast.success('Photo approved');
      setReviewedCount(prev => prev + 1);
      setPendingCount(prev => Math.max(0, prev - 1));
      fetchNext();
    } catch {
      toast.error('Failed to approve photo');
      setLoading(false);
    }
  }, [current, loading, fetchNext]);

  const handleReject = useCallback(async () => {
    if (!current || loading) return;
    setLoading(true);
    try {
      await PhotoModerationService.moderate(current.id, 'REJECTED');
      toast.success('Photo rejected');
      setReviewedCount(prev => prev + 1);
      setPendingCount(prev => Math.max(0, prev - 1));
      fetchNext();
    } catch {
      toast.error('Failed to reject photo');
      setLoading(false);
    }
  }, [current, loading, fetchNext]);

  const handleClose = useCallback(() => {
    setCurrent(null);
    setQueueEmpty(false);
    onClose();
  }, [onClose]);

  usePhotoModerationKeyboard(handleApprove, handleReject, handleClose, open && !lightboxUrl);

  const sortedHistory = useMemo(
    () =>
      [...history].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      ),
    [history],
  );

  return (
    <Dialog fullScreen open={open} onClose={handleClose}>
      <AppBar sx={{ position: 'relative' }} color='default'>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant='h6'>
            Photo Moderation
          </Typography>
          <Typography variant='body2' sx={{ mr: 3 }}>
            {pendingCount} remaining | {reviewedCount} reviewed this session
          </Typography>
          <Box sx={{ mr: 2 }}>
            <Chip label='← A Reject' size='small' sx={{ mr: 1 }} />
            <Chip label='D Approve →' size='small' />
          </Box>
          <IconButton edge='end' color='inherit' onClick={handleClose} aria-label='close'>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        justifyContent='center'
        sx={{ flex: 1, p: 3 }}
      >
        {queueEmpty && (
          <Box textAlign='center'>
            <Typography variant='h4' gutterBottom>
              All caught up!
            </Typography>
            <Typography variant='body1' color='textSecondary' gutterBottom>
              No pending photos to review.
            </Typography>
            <Button variant='contained' onClick={handleClose} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        )}

        {current && !queueEmpty && (
          <>
            <Box display='flex' alignItems='center' gap={2} mb={3}>
              <Typography variant='h6'>
                <a
                  href={`/users/lookup?id=${current.userId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {current.userName || `User #${current.userId}`}
                </a>
              </Typography>
              <Chip label={`ID: ${current.userId}`} size='small' variant='outlined' />
              <Chip
                label={current.source}
                color={current.source === 'UPLOAD' ? 'primary' : 'default'}
                size='small'
              />
            </Box>

            {/* Side-by-side photos */}
            <Box display='flex' gap={4} mb={3} justifyContent='center' alignItems='flex-start'>
              <Box textAlign='center'>
                <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                  Previous Photo
                </Typography>
                {current.previousPhotoUrl ? (
                  <Box
                    component='img'
                    src={current.previousPhotoUrl}
                    alt='Previous'
                    sx={{
                      width: 400,
                      height: 400,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: colors.gray[300],
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 400,
                      height: 400,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: colors.gray[300],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: colors.gray[100],
                    }}
                  >
                    <Typography color='textSecondary'>
                      {current.source === 'RENEWAL' ? 'Periodic review' : 'First photo'}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box textAlign='center'>
                <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                  {current.source === 'RENEWAL' ? 'Current Photo' : 'New Photo'}
                </Typography>
                {current.newPhotoUrl ? (
                  <Box
                    component='img'
                    src={current.newPhotoUrl}
                    alt='New'
                    sx={{
                      width: 400,
                      height: 400,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'primary.main',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 400,
                      height: 400,
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: colors.gray[300],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: colors.gray[100],
                    }}
                  >
                    <Typography color='textSecondary'>No photo</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Photo history */}
            {sortedHistory.length > 0 && (
              <Box sx={{ width: '100%', maxWidth: 880, mb: 3 }}>
                <Box display='flex' alignItems='center' justifyContent='center' gap={1} mb={1}>
                  <Link
                    component='button'
                    type='button'
                    underline='hover'
                    onClick={() => setHistoryExpanded(prev => !prev)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <Typography variant='body2'>
                      Photo history ({sortedHistory.length})
                    </Typography>
                    {historyExpanded ? (
                      <ExpandLess fontSize='small' />
                    ) : (
                      <ExpandMore fontSize='small' />
                    )}
                  </Link>
                  {!historyExpanded && sortedHistory.length > 2 && (
                    <Chip
                      label='Multiple uploads — review'
                      color='warning'
                      size='small'
                    />
                  )}
                </Box>
                {historyExpanded && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      overflowX: 'auto',
                      pb: 1,
                      px: 1,
                    }}
                  >
                    {sortedHistory.map(photo => (
                      <Box
                        key={photo.id}
                        sx={{ flex: '0 0 auto', textAlign: 'center', width: 80 }}
                      >
                        {photo.photoUrl ? (
                          <ButtonBase
                            onClick={() => setLightboxUrl(photo.photoUrl)}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '2px solid',
                              borderColor: STATUS_BORDER_COLOR[photo.status] || DEFAULT_BORDER_COLOR,
                            }}
                          >
                            <img
                              src={photo.photoUrl}
                              alt={`Upload ${photo.id}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </ButtonBase>
                        ) : (
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 1,
                              bgcolor: colors.gray[100],
                              border: '2px solid',
                              borderColor: STATUS_BORDER_COLOR[photo.status] || DEFAULT_BORDER_COLOR,
                            }}
                          />
                        )}
                        <Chip
                          label={photo.status}
                          color={STATUS_CHIP_COLOR[photo.status] || 'default'}
                          size='small'
                          sx={{ mt: 0.5, height: 18, fontSize: 10 }}
                        />
                        <Typography
                          variant='caption'
                          display='block'
                          color='textSecondary'
                          sx={{ fontSize: 10 }}
                        >
                          {new Date(photo.created).toLocaleDateString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Action buttons */}
            <Box display='flex' gap={4}>
              <Button
                variant='contained'
                color='error'
                size='large'
                startIcon={<ThumbDown />}
                onClick={handleReject}
                disabled={loading}
                sx={{ minWidth: 160, py: 1.5 }}
              >
                Reject
              </Button>
              <Button
                variant='contained'
                color='success'
                size='large'
                startIcon={<ThumbUp />}
                onClick={handleApprove}
                disabled={loading}
                sx={{ minWidth: 160, py: 1.5 }}
              >
                Approve
              </Button>
            </Box>
          </>
        )}

        {loading && !current && !queueEmpty && (
          <Typography color='textSecondary'>Loading...</Typography>
        )}
      </Box>

      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth='md'
      >
        {lightboxUrl && (
          <ButtonBase
            onClick={() => setLightboxUrl(null)}
            sx={{ display: 'block' }}
          >
            <img
              src={lightboxUrl}
              alt='Full size user upload'
              style={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '90vh',
              }}
            />
          </ButtonBase>
        )}
      </Dialog>
    </Dialog>
  );
}

export default PhotoModerationModal;
